// §584 — HUB /medici e /medici/{specialita}.
//
// Le pagine città/specialità (/medici/{specialita}/{citta}) esistevano già ed
// erano linkate fra loro, ma i due livelli superiori della gerarchia rispondevano
// 404: la struttura dichiarava un genitore (`/medici` è il canonical di fallback
// in GeoLanding) che non esisteva. Questi hub sono generati INTERAMENTE dai dati
// di pubblicazione già presenti in src/data/geoSeoData.ts: nessun contenuto
// inventato, nessuna logica per singola specialità o città, nessuna pagina vuota
// (una specialità senza città pubblicate non compare).

import { Link } from "@/lib/router-compat";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { PublicFooter } from "@/components/PublicFooter";
import { ArrowRight, MapPin } from "lucide-react";
import {
  getAllPublishedCitiesForSpecialty,
  getPublishedGeoCombinations,
  getSpecialtyBySlug,
  type SpecialtyGeoData,
} from "@/data/geoSeoData";

/** Specialità con almeno una pagina città pubblicata, in ordine deterministico. */
export function publishedGeoSpecialties(): SpecialtyGeoData[] {
  const seen = new Map<string, SpecialtyGeoData>();
  for (const { specialty } of getPublishedGeoCombinations()) {
    if (!seen.has(specialty.slug)) seen.set(specialty.slug, specialty);
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name, "it"));
}

export function GeoRootHub() {
  const specialties = publishedGeoSpecialties();

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Breadcrumbs items={[{ label: "Medici" }]} className="mb-4" />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground-soft">
            Refertius per i medici, città per città
          </h1>
          <p className="mt-3 text-base text-muted-foreground max-w-3xl">
            Le pagine locali disponibili riguardano le specialità con nomenclatore regionale
            attivo in piattaforma. Da qui si raggiunge l&apos;elenco delle città pubblicate per
            ciascuna specialità.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {specialties.map((s) => {
            const cities = getAllPublishedCitiesForSpecialty(s.slug);
            return (
              <Card key={s.slug} className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <h2 className="text-lg font-semibold mb-1">{s.name}</h2>
                  <p className="text-sm text-muted-foreground mb-4">
                    {cities.length} {cities.length === 1 ? "città disponibile" : "città disponibili"}
                  </p>
                  <Link
                    to={`/medici/${s.slug}`}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                  >
                    Vedi le città per {s.name}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <section className="mt-12 p-6 rounded-lg bg-muted/30 border">
          <h2 className="text-xl font-semibold mb-2">Tutte le specialità del catalogo</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Le pagine di specialità coprono l&apos;intero catalogo clinico, anche dove non esiste
            ancora una pagina locale.
          </p>
          <Link
            to="/specialita"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            Vai alle specialità
            <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}

export function GeoSpecialtyHub({ specialtySlug }: { specialtySlug: string }) {
  const specialty = getSpecialtyBySlug(specialtySlug);
  const cities = specialty ? getAllPublishedCitiesForSpecialty(specialtySlug) : [];
  if (!specialty || cities.length === 0) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <Breadcrumbs
            items={[
              { label: "Medici", href: "/medici" },
              { label: specialty.name },
            ]}
            className="mb-4"
          />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground-soft">
            Refertius {specialty.preposition} {specialty.name}: città disponibili
          </h1>
          <p className="mt-3 text-base text-muted-foreground max-w-3xl">
            Ogni pagina descrive il contesto sanitario locale e il funzionamento di Refertius
            per {specialty.name.toLowerCase()} in quella città.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cities.map((c) => (
            <Link key={c.slug} to={`/medici/${specialty.slug}/${c.slug}`}>
              <Card className="hover:shadow-md transition-shadow h-full">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="h-4 w-4 text-primary" aria-hidden="true" />
                    <h2 className="text-lg font-semibold">{c.name}</h2>
                  </div>
                  <p className="text-sm text-muted-foreground">{c.region}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <section className="mt-12 p-6 rounded-lg bg-muted/30 border">
          <h2 className="text-xl font-semibold mb-2">
            Pagina di specialità {specialty.preposition} {specialty.name}
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Contenuto clinico completo, esempi di referto e domande frequenti.
          </p>
          <Link
            to={`/specialita/${specialty.slug}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            Vai a {specialty.name}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
